import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AvatarComponent, FieldComponent } from './ui/ui';

export interface ProjectNameDialogData {
  title: string;
  confirmText: string;
  name?: string;
  /** Material Symbol for the header block. Defaults to the "new project" one. */
  icon?: string;
  /** One line under the title saying what this will do. */
  subtitle?: string;
}

/** The longest name the API stores. */
const MAX_NAME = 100;

/** Small name prompt reused for creating and renaming a project. Closes with the trimmed name. */
@Component({
  selector: 'app-project-name-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    AvatarComponent,
    FieldComponent
  ],
  templateUrl: './project-name-dialog.component.html',
  styleUrl: './project-name-dialog.component.scss'
})
export class ProjectNameDialogComponent {
  name = '';
  readonly MAX_NAME = MAX_NAME;
  readonly icon: string;
  readonly subtitle: string;

  constructor(
    private ref: MatDialogRef<ProjectNameDialogComponent, string>,
    @Inject(MAT_DIALOG_DATA) public data: ProjectNameDialogData
  ) {
    this.onNameChange(data.name ?? '');
    this.icon = data.icon ?? 'add_circle';
    this.subtitle = data.subtitle
      ?? 'A project is a separate agent, with its own knowledge, settings and widget.';
  }

  /* Precomputed rather than read through a getter — see the angular-performance
     skill. Refreshed from the input's own change event. */
  trimmedName: string | null = null;
  /** Empty until something is typed — "0 / 100" under a blank field is noise. */
  countLabel = '';

  onNameChange(value: string): void {
    this.name = value;
    this.trimmedName = value.trim() || null;
    this.countLabel = value.length ? `${value.length} / ${MAX_NAME}` : '';
  }

  cancel(): void { this.ref.close(); }

  submit(): void {
    const trimmed = this.trimmedName;
    if (trimmed) this.ref.close(trimmed);
  }
}
